import React from 'react';
import PropTypes from 'prop-types';
import { toJS, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import Downshift from 'downshift';
import { MSInputSimple } from './msInputSimple';
import { post } from '../utils/query';
import { debounce } from '../utils/debounce';
import { Icon } from './icon';
import { ACOptions } from './msInputACOptions';
import './msInputAC.css';

export const emptyElement = {};

let wsName = 'AC.php';

export function setACWSName(name) { wsName = name; }

const isValidItem = (value, acParam) => {
  if (!value || !acParam.keyCol) return false;
  return acParam.keyCol.every(k => value[k] || value[k] === 0);
};

const addDependencyToQuery = (query, form) => {
  const newQuery = { ...query };
  // dependOn format: { column_name: '123', key_col: 'Key columns of dependent field if it's empty we take column_name' }
  if (newQuery?.dependOn?.length) {
    const filter = newQuery.dependOn.map((field) => {
      if (field.use_for_filter === false) {
        return null;
      }
      const formValue = form[field.column_name];
      const value = formValue === true ? 1 : formValue;
      const keyCol = field.key_col || field.column_name;
      if (value == null || value === '' || value === false || !keyCol) {
        return null;
      }

      const searchMode = field.search_mode || '=';
      const searchPrefix = field.search_prefix || '';
      const searchValue = searchMode === 'like' ? `%${value}${searchPrefix}%` : value;
      return [keyCol, searchMode, searchValue];
    });
    const dependencyFilter = filter.filter(Boolean);
    newQuery.filter = [...(newQuery.filter || []), ...dependencyFilter];
    if (dependencyFilter.length) {
      newQuery.selectDistinct = true;
    }
    delete newQuery.dependOn;
  }

  return newQuery;
};
const stopPropagation = ev => ev.stopPropagation();

// for test purposes, allows to set values when doing puppeter tests
export const __testSetACFactory = (acParam, handleStateChange) => invalue => {
  let value = {};
  if (Array.isArray(invalue)) {
    // ["hydrant", "123"]
    acParam.keyCol.forEach((k, i) => value[k] = invalue[i]);
  } else if (typeof (invalue) === 'string') {
    // "C"
    acParam.keyCol.forEach(k => value[k] = invalue);
  } else {
    // {"asset_type": "hydrant", "asset_id": "h123"}
    value = invalue;
  }

  let state = {
    highlightedIndex: null,
    // if specified, will load twice
    // inputValue: getText(acParam.textColInput || acParam.textCol, value),
    selectedItem: value,
    isOpen: false,
    type: '__autocomplete_click_item__',
  };
  handleStateChange(state);
};

// for test purposes, allows to set values when doing puppeter tests
export const __testGetACFactory = (component) => () => {
  if (component.state.selectedItem) return getValue(component.props.acParam.keyCol, component.state.selectedItem);
  return null;
};

// document.getElementById("acInputID_wk_in_progress").__testSetAC("C")
// document.getElementById("acInputID_assign_to").__testSetAC({"user_name":"BRETT"})
// document.getElementById("acInputID_assign_to").__testSetAC(null)



// ACParam example
// ==============================================================
// {
//   "table": "asset_hdr",                      // required
//   "keyCol": ["asset_type","asset_id"],     // this or mapper function required
//   "searchCol": ["asset_type","asset_id"],    // fields by which to search
//   "textCol": ["asset_type","asset_id"],      // fields to put into text
//   "entered": "As",                           // goes to server, entered value
//   "filter": [                                // additional filter
//     ["name", "like", "T%"],
//     ["votes", "<>", 100],
//     ["votes", "=", 100]
//   ]
//   // only for server
//   "value": {"asset_type":"hydrant", "asset_id":"001"}
// }

// jumping cursor
// please check this issue
// https://codesandbox.io/s/989238nx64

const initialLoad = [];

export function formatACtoDQ(query, showInactive) {
  const newQuery = {};
  const { table, value, keyCol, valueCol, searchCol, textCol, textColInput, filter, searchStartWith, entered, ...rest } = query;
  newQuery.select = [...new Set([...keyCol, ...textCol, ...(valueCol || []), ...(textColInput || []), ...searchCol])];
  newQuery.table = table;
  newQuery.search = { entered, searchCol, startWith: searchStartWith };
  newQuery.filter = [...(filter || [])];

  if (showInactive) {
    // get description for inactive items
    newQuery.filter = newQuery.filter.filter(d => d[0] && d[0] !== 'active_flag');
  }
  if (value) {
    Object.keys(value).forEach(d => newQuery.filter.push([d, '=', value[d]]));
  }
  return { ...newQuery, ...rest };
}

let initialLoadFuncQuery = debounce(async () => {
  let loadAr = [...initialLoad];
  initialLoad.length = 0;
  let queryAr = loadAr.map(x => x.query);

  if (queryAr.length > 100) {
    console.warn('too many requests', loadAr);
    queryAr = queryAr.slice(0, 20);
  }

  // for portal
  if (wsName !== 'AC.php') {
    for (let i = 0; i < loadAr.length; i += 1) {
      post(queryAr[i].table + '.php', queryAr[i], { skipIndicator: true }).then(data => {
        let { cb, control } = loadAr[i];
        cb.call(control, data.value);
      }).catch(e => {
        loadAr.forEach(x => x.control.setState({ isLoading: false }));
        throw e;
      });
    }
    return;
  }

  const res = await post('AC.php', queryAr, { skipIndicator: true });
  if (res?.length) {
    for (let i = 0; i < loadAr.length; i += 1) {
      let data = res[i].value;
      let { cb, control } = loadAr[i];
      cb.call(control, data);
    }
  } else {
    loadAr.forEach(x => x.control.setState({ isLoading: false }));
    throw res.error || res.warning;
  }
}, 200);

export function initialLoadFunc(control, query, cb) {
  initialLoad.push({ control, query, cb });
  initialLoadFuncQuery();
}

export function getText(textColOrigin, r, inList = false, textColFormat, mapper) {
  if (r == null) return '';

  // need mapping for multiselect, usual select works without mapping somehow
  const textCol = textColOrigin.map(d => ((d?.split('.')?.length === 2) ? d.split('.')[1] : d));
  let textColMapped = mapper ? textCol.map(d => mapper[d]) : textCol;

  if (Object.keys(r).length === 0) return inList ? '(empty)' : '';
  if (textColFormat) {
    r = textColMapped.map(k => (textColFormat[k] ? textColFormat[k](r[k]) : r[k])).filter(v => !!v || v === 0).join(' - ');
  } else {
    r = textColMapped.map(k => r[k]).filter(v => !!v || v === 0).join(' - ');
  }

  return r;
}

export function getValue(keyCol, r) {
  // if (r == null) return '';
  if (r == null) r = {};
  r = keyCol.reduce((a, k) => { a[k] = r[k] || null; return a; }, {});
  return r;
}

export function getValueString(keyCol, r, sep = '') {
  if (r == null) return '';
  r = keyCol.map(k => {
    if (typeof (r[k]) === 'object') {
      return null
    }

    return r[k];
  }).join(sep);
  return r;
}

export function valueEquals(keyCol, it, it2, mapper) {
  // need mapping for multiselect, usual select works without mapping somehow
  const keyColMapped = mapper ? keyCol.map(d => mapper[d] || d) : keyCol;

  if (it == null && it2 == null) return true;
  if (it == null || it2 == null) return false;
  return keyColMapped.every(k => it[k] === it2[k]);
}

export function mapValueToServer(value, mapper) {
  if (mapper == null) return value;
  const mapperRev = {};
  Object.keys(mapper).forEach(d => { mapperRev[mapper[d]] = d; });
  const newValue = { ...value };
  Object.keys(value).forEach(d => {
    if (mapperRev[d]) {
      const colName = mapperRev[d] || d;
      newValue[colName] = value[d];
    }
  });
  return newValue;
}

function mapValueToForm(value, mapper) {
  const newValue = {};
  Object.keys(value).forEach(d => { newValue[mapper[d] || d] = value[d]; });
  return newValue;
}

export const MSInputAC = observer(class MSInputAC extends React.Component {
  static propTypes = {
    acFree: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    mapper: PropTypes.object,
    acParam: PropTypes.shape({
      table: PropTypes.string,
      keyCol: PropTypes.arrayOf(PropTypes.string),
      valueCol: PropTypes.arrayOf(PropTypes.string),
      searchCol: PropTypes.arrayOf(PropTypes.string),
      textCol: PropTypes.arrayOf(PropTypes.string),
      filter: PropTypes.arrayOf(PropTypes.array),
      value: PropTypes.object,
    }).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    form: PropTypes.object,
    belowFieldCont: PropTypes.object,
    // acParam, acFree, mapper, value, nostyle, readonly, disabled, ...rest
  };

  constructor(props) {
    super(props);

    makeObservable(this, { componentWillUnmount: false, queryItemsDeb: false, queryItems: false, updateOptionsStyle: false, getOptionsStyle: false, componentDidMount: false, tryLoadText: false, getInputID: false, handleOuterClick: false, handleTabPress: false, selectFirstOption: false, handleToggle: false, render: false, 
      handleStateChange: action
    });

    this.state = {
      items: [],
      inputValue: '',
      selectedItem: null,
      isOpen: false,
      isLoading: false,
    };
    this.inputRef = React.createRef();
    this.valueInitialized = false;
    this.onChangeDebaunce = props.onChange ? debounce(props.onChange, 300) : props.onChange;
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  queryItemsDeb = debounce(v => this.queryItems(v), 250)
  queryItems = (v) => {
    let { acParam: acParam0, acFree, empty = true } = this.props;

    let acParam = {
      ...acParam0,
      entered: v,
    };

    let { mapper, ...query } = acParam;
    query.count = 0;
    this.setState({ items: [], isLoading: true });

    const ws = acParam0.table.indexOf('DQ_') > -1 ? acParam0.table + '.php' : 'AC.php';
    query = addDependencyToQuery(query, this.props.form);
    post(ws, formatACtoDQ(query), { skipIndicator: true })
      .then(res => {
        if (this.unmounted) return;
        const { selectedItem } = this.state;
        let items = [];
        items = [
          acFree ? null : (selectedItem !== emptyElement ? selectedItem : null),
          ...res.value.filter(it => !valueEquals(acParam.keyCol, it, selectedItem)),
        ].filter(x => isValidItem(x, acParam));

        // if (empty !== false) items.unshift(emptyElement); // add empty element
        if (empty !== false && (!selectedItem || Object.keys(selectedItem).length !== 0)) items.unshift(emptyElement); // add empty element

        this.setState({ items, isLoading: false });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }

  updateOptionsStyle = (ch) => {
    ch.style.top = this.getOptionsStyle(true).top + 'px';
  }

  getOptionsStyle = (vis, optionsWidth) => {
    const { optionsHeight: optionsHeightProp } = this.props;
    let c = this.inputRef.current;
    const rect = c.getBoundingClientRect();
    // rect.x and rect.y don't work in edge, replaced with rect.left and rect.top
    // window.scrollY is the same in Edge and Chrome
    // document.body.scrollTop always gives 0 in Chrome

    const scroll = window.scrollY;

    let optionsHeight = optionsHeightProp || 300;
    let { clientWidth } = document.documentElement;
    let { clientHeight } = document.documentElement;

    let maxWidth = clientWidth - rect.left;
    let minWidth = optionsWidth || 300;

    let style = {
      left: rect.left,
      width: rect.width,
      display: vis ? 'block' : 'none',
      minWidth: Math.min(minWidth, maxWidth),
      height: optionsHeight,
      position: 'absolute',
    };

    if (clientHeight < (rect.y + rect.height + optionsHeight)) {
      style.top = rect.top + scroll - optionsHeight;
    } else {
      style.top = rect.top + scroll + rect.height;
    }

    return style;
  }

  componentDidMount() {
    // to give ability to set value outside
    if (this.inputRef.current) {
      this.inputRef.current.__testSetAC = __testSetACFactory(this.props.acParam, this.handleStateChange);
      this.inputRef.current.__testGetAC = __testGetACFactory(this);
    }
  }

  tryLoadText = () => {
    let { acParam, form, mapper } = this.props;
    let { selectedItem } = this.state;

    // it will be null if no value
    if (selectedItem) {
      const textCol = acParam.textColInput || acParam.textCol;

      let allColsExists = textCol.every(tc => typeof (selectedItem[tc]) !== 'undefined' && selectedItem[tc] !== '');
      // populate text columns from form
      if (!allColsExists) {
        textCol.forEach(c => { if (!selectedItem[c] && selectedItem !== '0') selectedItem[c] = (mapper?.[c] ? form[mapper[c]] : form[c]) || ''; });
      }
      allColsExists = textCol.every(tc => typeof (selectedItem[tc]) !== 'undefined' && selectedItem[tc] !== '');


      // we have everything needed for inputValue
      if (allColsExists) {
        this.setState({ inputValue: getText(textCol, selectedItem), selectedItem });
      } else {
        if (acParam.skipLoadingText) return;

        // we done have all we need for textValue
        // ?? should we query by keyCol? we can have many keyCol and they can be rewritten
        // let value = getValue(acParam.keyCol, selectedItem);
        // let value = getValue(acParam.keyCol.filter(d => acParam.searchCol.includes(d)), selectedItem);
        let value = getValue(acParam.keyCol, selectedItem);
        let query = { ...acParam, value, count: 0 };
        query = addDependencyToQuery(query, form);
        // keep queries in array,
        // then run them at once
        // got as array and load in proper field.
        initialLoadFunc(this, formatACtoDQ(query, true), items => {
          if (this.unmounted) return;
          // find selected item
          let selItem = items.find(it => valueEquals(acParam.keyCol, it, value));
          let state = { items };

          if (selItem) {
            state.inputValue = getText(textCol, selItem);
            state.selectedItem = selItem;
          }
          // set items
          this.setState(state);
        });
      }
    }
  }

  handleStateChange = (changes) => {
    let { acParam, onChange, mapper, acFree, empty = true } = this.props;
    let { items } = this.state;
    let newState = {};
    let onChangeResult = null;

    // escape button
    if (changes.type === Downshift.stateChangeTypes.keyDownEscape) {
      let inputValue = getText(acParam.textColInput || acParam.textCol, this.state.selectedItem);
      this.setState({ isOpen: false, inputValue });
      return;
    }

    // somebody selected something
    if (changes.hasOwnProperty('selectedItem')) {
      newState.inputValue = getText(acParam.textColInput || acParam.textCol, changes.selectedItem);

      // added JSON.parse(JSON.stringify()) because empty value doesn't work otherwise
      newState.selectedItem = JSON.parse(JSON.stringify(changes.selectedItem));
      newState.isOpen = false;
    }

    // typed into field
    if (changes.hasOwnProperty('inputValue')) {
      // this is when somebody typed in something into field
      // query
      this.queryItemsDeb(changes.inputValue);
      // set chagnes
      newState.inputValue = changes.inputValue || '';
      newState.isOpen = true;

      // if free entry or selected == false

      // why do we remove selected item when typing? field loses value when AC is open because of this
      // commented for now, acFree is not used yet
      /*
      if (acFree || !newState.inputValue) {
        newState.selectedItem = null;
      }
      if (!acFree && !newState.inputValue) {
        newState.selectedItem = null;
      }
      */
    }

    // sometimes worth, when nothing changed except isOpen
    // we need to update our isOpen
    // example: somebody selected already selected item
    if (changes.hasOwnProperty('isOpen')) {
      newState.isOpen = changes.isOpen;
    }

    // call handler
    if (onChange && newState) {
      let val = null;

      if (acFree) {
        const isChangeInput = changes.type === Downshift.stateChangeTypes.changeInput;
        const inputValue = { [acParam.keyCol[0]]: newState.inputValue !== undefined ? newState.inputValue : this.state.inputValue };
        let selItem = isChangeInput ? inputValue : (newState.selectedItem || this.state.selectedItem);
        // acFree, set only first keyCol when value is not selected
        val = getValue(acParam.keyCol, selItem);

        // map value to form
        if (mapper) { val = mapValueToForm(val, mapper); }

        onChangeResult = this.onChangeDebaunce(val);
      } else if (newState.hasOwnProperty('selectedItem') && newState.selectedItem !== emptyElement) {
        // we call change only when selected value changed
        let selItem = newState.selectedItem;
        val = getValue(acParam.valueCol || acParam.keyCol, selItem);

        // make selected element the first;
        newState.items = [
          selItem !== emptyElement ? selItem : null, // selected element
          ...items.filter(it => it !== selItem && it !== emptyElement), // filter empty and selected
        ].filter(x => isValidItem(x, acParam));

        if (empty !== false) newState.items.unshift(emptyElement); // add empty element
        // if (empty !== false && (!selItem || Object.keys(selItem).length !== 0)) newState.items.unshift(emptyElement); // add empty element

        // map value to form
        if (mapper) { val = mapValueToForm(val, mapper); }
        onChangeResult = onChange(val);
      }
    }

    if (onChangeResult !== false) {
      this.setState(newState);
      // set focus on input again if autocomplete is free entry
      if (acFree) {
        setTimeout(() => {
          const el = document.getElementById(this.getInputID()?.replace(/:/g, '__'));
          el?.focus?.();
        }, 1);
      }
    }
  };

  getInputID = () => {
    const { id, name } = this.props;
    return id || ('acInputID_' + name);
  }

  handleOuterClick = () => {
    // close if opened
    let { isOpen, selectedItem, inputValue } = this.state;
    let { acParam, acFree } = this.props;
    if (isOpen) this.setState({ isOpen: !isOpen });
    // reset input value if it is changed but value is selected
    if (inputValue && !acFree) {
      this.selectFirstOption();
      return;
    }
    if (!acFree && selectedItem != null) {
      const textColInput = acParam.textColInput || acParam.textCol;
      let text = getText(textColInput, selectedItem);
      if (inputValue !== text) {
        this.setState({ inputValue: text });
      }
    }
  }
  handleTabPress = (e) => {
    // select option when tab
    if (e.key !== 'Tab') return;

    this.selectFirstOption();
  }
  selectFirstOption = () => {
    const { items, selectedItem, inputValue } = this.state;
    const { keyCol, valueCol, textColInput, textCol } = this.props.acParam;
    let options = items.filter(d => keyCol.some(k => d && d[k]));
    if (selectedItem) options = options.filter(d => keyCol.every(k => d[k] != selectedItem[k]));

    if (!options.length) return;
    const item = options[0];

    const text = getText(textColInput || textCol || keyCol, item);
    this.setState({ selectedItem: item, inputValue: text });

    let val = null;
    val = getValue(valueCol || keyCol, item);
    if (this.props.mapper) { val = mapValueToForm(val, this.props.mapper); }
    if (val) this.props.onChange(val);
  }

  handleToggle = () => {
    let { isOpen } = this.state;
    if (!isOpen) this.queryItems('');
    isOpen = !isOpen;
    this.setState({ isOpen });
    if (isOpen) {
      this.inputRef.current.focus();
    }

    // empty field when open, return when closed
    let { acFree, acMulti } = this.props;
    if (!acFree && !acMulti) {
      let { selectedItem, inputValue } = this.state;
      let { acParam } = this.props;

      // if open, empty input value
      if (isOpen) {
        this.setState({ inputValue: ' ' });
      }
      // if closed, restore input value
      else {
        // has selected value
        if (selectedItem != null) {
          const textColInput = acParam.textColInput || acParam.textCol;
          let text = getText(textColInput, selectedItem);
          if (inputValue !== text) {
            this.setState({ inputValue: text });
          }
        }
      }
    }
  }

  render() {
    let { acParam, acFree, mapper, form, nostyle, readOnly, disabled, onChange, belowFieldCont, type, empty, style, optionsWidth, skipDefaultClass, ...rest } = this.props;
    let { items, inputValue, selectedItem, isOpen, isLoading } = this.state;

    const { textColFormat, optionsExtra } = acParam;
    optionsWidth = optionsWidth || acParam.optionsWidth;
    const textColInput = acParam.textColInput || acParam.textCol;

    // validation
    // if (typeof (value) !== 'object') throw new Error('for AC fields use fld("fldName", true). 2nd parameter is true.');
    if (!form || typeof (form) !== 'object') { 
      throw new Error('"form" is required for AC fields, use fld("fldName", true) - 2nd parameter is true. ' + rest.name);
    }

    // map, if there is a mapper
    const value = mapValueToServer(form, mapper);

    // check if value changed outsize
    // THIS iS BAD PRACTIVE, BUT There is no way
    // right now to check if value changed withing a value, because value is an object
    // and componentDidUpdate is not called when internal field changed, but render is called.
    let valueStr = getValueString(acParam.keyCol, value);
    let selectedValueStr = getValueString(acParam.keyCol, selectedItem);
    // check that form has values for each key, otherwise it will stuck in a loop

    if (selectedValueStr !== valueStr && (!selectedItem || acParam.keyCol.every(d => selectedItem[d]))) {
      let newValue = valueStr ? getValue(acParam.keyCol, value) : null;
      setTimeout(() => {
        if (this.unmounted) return;
        let newState = { selectedItem: newValue, inputValue: getText(textColInput, newValue || {}) };
        this.setState(newState, this.tryLoadText);
      });
    }
    let options = [...items];
    // remove records with empty keys (garbage data)
    options = options.filter((d, i) => acParam.keyCol.every(key => i < 2 || d[key] || d[key] === 0 || d[key] === '0'));

    if (optionsExtra) {
      const first = selectedItem ? options[0] : false;
      if (first) options.shift();
      options = [...optionsExtra.filter(d => !valueEquals(acParam.keyCol, d, selectedItem)), ...options];
      if (first) options = [first, ...options];
    }
    if (acParam.filterFunc) {
      options = options.filter(d => acParam.filterFunc(d));
    }

    const cl = {
      'ms-input-ac_cont': 1,
      'msfield-input': !skipDefaultClass,
      // 'no-style': nostyle,
    };

    const inputID = this.getInputID();

    const belowFieldDescription = acParam.textColLong ? getText(acParam.textColLong, selectedItem, true) : '';

    return (
      <Downshift
        onStateChange={this.handleStateChange}
        onOuterClick={this.handleOuterClick}
        itemToString={item2 => {
          const item = item2;
          let val = getValueString(acParam.keyCol, item, '-');
          let text = '';
          if (val === '') {
            text = '';
          } else {
            text = getText(textColInput, item);
          }
          return text;
        }}
        inputValue={inputValue}
        selectedItem={selectedItem}
        isOpen={isOpen}
      >
        {({
          getInputProps,
          getItemProps,
          // getLabelProps,
          getMenuProps,
          isOpen: isOpen2,
          // inputValue,
          highlightedIndex,
          selectedItem: selectedItem2,
          }) => {
          let inputProps = getInputProps({
            onBlur: this.handleBlur,
            onKeyDown: this.handleTabPress,
          });
          return (
            <div className={classnames(cl)} onClick={stopPropagation} onKeyDown={stopPropagation} role="presentation">
              <MSInputSimple key="field" label={rest.label || rest.name || 'Asset Id'} inputRef={this.inputRef} {...rest} style={style} {...inputProps} id={inputID} skipDefaultClass disabled={disabled || readOnly} />
              { isOpen2 && !readOnly && !disabled && (
                <ACOptions key="options" onContainerScroll={this.updateOptionsStyle}>
                  <div className="ms-input-ac-options" style={this.getOptionsStyle(isOpen2, optionsWidth)} {...getMenuProps()} id="ACOptionsDiv">
                    { isLoading && (
                      <button type="submit" className="ms-input-ac-icon__loading">
                        <Icon>hourglass_empty</Icon>
                      </button>
                    )}
                    {/* Iterate items */}
                    { options && options.map((item, index) => (
                      <div
                        key={index}
                        {...getItemProps({
                          // key: getValueString(acParam.keyCol, item, ':'),
                          key: index, // for stock lookup
                          index,
                          item,
                          className: 'ms-input-ac-option',
                          style: {
                            backgroundColor: highlightedIndex === index ? 'lightgray' : 'white',
                            fontWeight: item !== emptyElement && selectedItem2 === item ? 'bold' : 'normal',
                            borderBottom: 0,
                          },
                        })}
                      >
                        {/* send both item and text */}
                        {acParam.textFunc ? acParam.textFunc(item, getText(acParam.textCol, item, true, textColFormat)) : getText(acParam.textCol, item, true, textColFormat)}
                      </div>
                    ))}
                    {/* items.length === 0 && <div className="no-records-found">No records found</div> */}
                  </div>
                </ACOptions>
              )}
              {!disabled && (
                <Icon className="ms-input-ac-icon__expand" style={{ backgroundColor: style?.backgroundColor }} onClick={this.handleToggle}>arrow_drop_down</Icon>
              )}
              {belowFieldCont}
              {belowFieldDescription && <div className="ms-input-ac__longdescr">{belowFieldDescription}</div>}
            </div>
          );
        }}
      </Downshift>
    );
  }
});

MSInputAC.displayName = 'MSInputAC';
